import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for Alloy module to handle 401 errors
const alloyAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for Alloy module
alloyAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// Alloy APIs
const getAlloyList = (page, pagesize, search, ordering) => {
  return alloyAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/alloy/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getAlloyById = id => {
  return alloyAxiosInstance.get(`${API_URL}/api/${API_VERSION}/alloy/${id}/`, {
    headers: authHeader(),
  });
};

const addAlloy = formData => {
  return alloyAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/alloy/`,
    formData,
    { headers: authHeader() },
  );
};

const updateAlloy = (id, formPayload) => {
  return alloyAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/alloy/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchAlloy = (id, formPayload) => {
  return alloyAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/alloy/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const deleteAlloy = id => {
  return alloyAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/alloy/${id}/`,
    { headers: authHeader() },
  );
};

const getAlloyDropdown = (filterKey) => {
  return alloyAxiosInstance.get(`${API_URL}/api/${API_VERSION}/alloy/dropdown/`, {
    headers: authHeader(),
    params: {
      ...(filterKey && { filter_key: filterKey }),
    },
  });
};

const getAlloyArchiveList = (page, pagesize, search, ordering) => {
  return alloyAxiosInstance.get(`${API_URL}/api/${API_VERSION}/alloy-archive/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getAlloyArchiveById = id => {
  return alloyAxiosInstance.get(`${API_URL}/api/${API_VERSION}/alloy-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreAlloy = ids => {
  return alloyAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/alloy/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveAlloy = ids => {
  return alloyAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/alloy/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportAlloy = formData => {
  return alloyAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/alloy/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return alloyAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/alloy/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return alloyAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/alloy/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return alloyAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/alloy/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const alloyService = {
  getAlloyList,
  getAlloyById,
  addAlloy,
  updateAlloy,
  patchAlloy,
  deleteAlloy,
  getAlloyDropdown,
  getAlloyArchiveList,
  getAlloyArchiveById,
  restoreAlloy,
  bulkArchiveAlloy,
  bulkImportAlloy,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default alloyService;
